import { forwardRef, type KeyboardEvent } from "react";
import RankInputControl from "@/elements/RankInputControl";
import type { RatingInputMode } from "@/sections/rankInput";

type WordSquareWord = {
   term?: string;
   description?: string;
};

type WordSquareData = {
   id: number;
   words: WordSquareWord[];
};

type WordSquareContent = {
   oops?: {
      missing?: string;
      repeated?: string;
   };
};

type WordSquareProps = {
   content: WordSquareContent;
   data: WordSquareData;
   id?: number;
   lang?: string;
   sectionData: Record<string, unknown>;
   setValue: (id: number, value: string | number) => void;
   isValid: boolean;
   showValidations: boolean;
   duplicate: boolean;
   value: string | number;
   index: number;
   tabIndex: number;
   onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => void;
   ratingInput: RatingInputMode;
};

const WordSquare = forwardRef<HTMLInputElement, WordSquareProps>((props, ref) => {
   const {
      content,
      data,
      sectionData,
      setValue,
      isValid,
      showValidations,
      duplicate,
      value,
      tabIndex,
      onKeyDown,
      ratingInput,
   } = props;
   if (!sectionData) return null;

   const isMissing = value === undefined || !value || value === -1;
   const fieldInvalid = showValidations && !isValid;

   return (
      <div className={`wordSquareColumn ${isValid ? "" : "invalid"}`}>
         {data.words.map((w, i) => (
            <div className="term" key={i}>
               <div className="title">{w.term}</div>
               <div className="definition">{w.description}</div>
            </div>
         ))}
         <div className="controls">
            <RankInputControl
               ref={ref}
               ratingInput={ratingInput}
               value={value}
               invalid={fieldInvalid}
               showValidations={showValidations}
               inputClassName="wordSquareInput"
               selectClassName="wordSquareInput checklistRatingSelect"
               id={`wordsquare-${data.id}`}
               name={`wordsquare-${data.id}`}
               ariaLabel={`Word square ranking for set ${data.id}`}
               tabIndex={tabIndex}
               dataId={data.id}
               isAdmin={!!sectionData?.isAdmin}
               onKeyDown={onKeyDown}
               onCommit={(next) => setValue(data.id, next === "" ? "" : next)}
            />
            {showValidations && isMissing && content.oops?.missing && (
               <div className="validation">{content.oops["missing"]}</div>
            )}
            {showValidations && duplicate && content.oops?.repeated && (
               <div className="validation">{content.oops["repeated"]}</div>
            )}
         </div>
      </div>
   );
});

WordSquare.displayName = "WordSquare";

export default WordSquare;
